/**
 * 기상 데이터 통합 훅 — 협약 대시보드 기능 #7 (현장 실측 + 기상청 병합).
 *
 * `GET /api/v1/weather/readings/?site_id=<id>` 는 `-time` 정렬이라 첫 행이 최신이다.
 * 각 관측은 `source` 로 출처를 구분한다:
 *   * `onsite`            → 현장 기상스테이션 실측 (매니져 경유, 우선)
 *   * `kma` / `open-meteo` → 기상청/외부 격자 예보 (폴백)
 * 백엔드 `resolve_site_weather` 는 "현장 실측 우선, 신선하지 않으면 KMA 폴백"으로
 * 해석장·안정도에 쓰지만, 대시보드는 최신 관측 1건을 그대로 보여주고 그 출처를
 * 라벨로 명시한다.
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "../client";

export type WeatherSource = "onsite" | "kma" | "open-meteo" | string;

export interface WeatherReading {
  id: number;
  time: string;
  station_id: number;
  site_id: number;
  /**
   * 이 관측을 올린 센서 함체. `null` = 사이트 단위 관측(기상청 격자, 또는 어느
   * 노드가 보냈는지 알 수 없는 구형 매니져 payload).
   *
   * ⚠️ null 을 아무 노드에나 귀속시키지 말 것 — 그 함체가 재지도 않은 바람이
   * 그 함체 이름으로 화면에 뜬다.
   */
  node_id: number | null;
  source: WeatherSource;
  temperature_c: number | null;
  humidity_pct: number | null;
  wind_speed_mps: number | null;
  wind_direction_deg: number | null;
  pressure_hpa: number | null;
  solar_intensity: string;
  /** Pasquill-Gifford 안정도 (A~F). */
  stability_class: string;
}

interface ReadingList {
  count?: number;
  next: string | null;
  previous: string | null;
  results: WeatherReading[];
}

/** 사이트 최신 기상 관측 1건 (최신순 첫 행). 1분 주기 갱신. */
export function useSiteWeather(siteId: number | undefined) {
  return useQuery({
    queryKey: ["ops", "weather", "latest", siteId ?? 0] as const,
    queryFn: () =>
      api
        .get<ReadingList | WeatherReading[]>("/weather/readings/", {
          params: { site_id: siteId, ordering: "-time", page_size: 1 },
        })
        .then((r) => {
          const rows = Array.isArray(r.data) ? r.data : r.data.results ?? [];
          return rows[0] ?? null;
        }),
    enabled: !!siteId,
    refetchInterval: 60_000,
  });
}

/**
 * 사이트의 **관측소별 최신 1건** — 노드 축 기상(관측소 4대) 화면의 재료.
 *
 * 왜 `useSiteWeather` 로 안 되나: 그쪽은 사이트 최신 1건이라, 노드마다 스테이션
 * 타일을 세우면 **네 타일에 같은 값**이 뜬다. 현장 구성은 그렇지 않다 — 풍향·풍속
 * 초음파는 상·중·하 1세트당 별도 함체 1대다(에어위드 2026-08-31 조합표).
 *
 * 왜 노드마다 훅을 부르지 않나: 노드 수는 런타임에 정해지므로 훅 규칙을 어긴다.
 * 서버가 관측소별 최신 1건을 한 번에 내려 주고(`/weather/readings/latest`),
 * 여기서 `node_id` 로 색인한다.
 *
 * 반환 Map 의 키가 `null` 인 항목은 사이트 공통 관측이다. 호출부는 그것을
 * **노드 타일에 베껴 적지 않는다** — 없는 사실이 되기 때문이고, 그 값이 필요한
 * 자리(우측 기상 카드)는 따로 있다.
 */
let headerSkewMs: number | null = null;
/** 응답 `Date` 헤더(초 단위)로 잰 「브라우저 시계 − 서버 시계」(ms). 같은 출처(웹)에서만 읽힌다 —
 *  앱(Capacitor)은 교차 출처라 CORS 가 Date 헤더를 숨긴다 → null.
 *  경보 질의(alerts.ts useAlerts, 30초 폴링)도 적는다(10-20) — 경보창 「현장 방송」 응답 없음 창을 서버 시각으로 재려고. */
export function noteServerClock(date: unknown) {
  const sv = typeof date === "string" ? Date.parse(date) : NaN;
  if (Number.isFinite(sv)) headerSkewMs = Date.now() - sv;
}
export function serverClockSkewFromHeader(): number | null {
  return headerSkewMs;
}

export function useSiteWeatherByNode(
  siteId: number | undefined,
  opts?: {
    /** 폴링 주기(ms). 기본 60초. 트윈의 바람 레이어는 10초로 당긴다.
     *  ⚠️ WS `sensor.reading` payload 의 `env.wd`/`env.ws` 에도 노드별 바람이 실린다(2~10초 간격 —
     *  2026-09-30 dev 스트림 확인). 관제 도면(nodeWindStore)은 WS 를 주로 쓰고 이 폴링(60초)은
     *  첫 값·끊김 대비용이다. 트윈의 10초 폴링은 아직 WS 를 쓰지 않는다. */
    refetchInterval?: number;
    /** false 면 폴링을 멈춘다(레이어 꺼짐·페이지 이탈). 캐시된 data 는 남는다. */
    enabled?: boolean;
  },
) {
  return useQuery({
    queryKey: ["ops", "weather", "byNode", siteId ?? 0] as const,
    queryFn: () =>
      api
        // ⚠️ 끝 슬래시 필수 — DRF 라우터가 만든 액션 URL 은 `latest/` 다. 빼면
        // APPEND_SLASH 가 매 폴링(60초)마다 301 을 한 번씩 더 태운다.
        .get<WeatherReading[] | ReadingList>("/weather/readings/latest/", {
          params: { site_id: siteId },
        })
        .then((r) => {
          noteServerClock(r.headers?.["date"]);
          const rows = Array.isArray(r.data) ? r.data : (r.data.results ?? []);
          const byNode = new Map<number | null, WeatherReading>();
          for (const row of rows) {
            const key = row.node_id ?? null;
            const prev = byNode.get(key);
            if (!prev || Date.parse(row.time) > Date.parse(prev.time)) byNode.set(key, row);
          }
          return byNode;
        }),
    enabled: !!siteId && (opts?.enabled ?? true),
    refetchInterval: opts?.refetchInterval ?? 60_000,
  });
}

/**
 * 출처 라벨 — 실측 우선/KMA 폴백을 화면 캡션으로 노출.
 *
 * 훅 파일이라 `useTranslation` 을 쓸 수 없으므로 **번역 키**만 돌려주고,
 * 호출부가 렌더 시점에 `t(labelKey)` 로 번역한다. 브랜드명(Open-Meteo)과
 * 미지의 출처 코드는 등록된 키가 없어 i18next 가 그대로 노출한다(폴백 유지).
 */
export function weatherSourceLabel(source: WeatherSource | undefined): {
  labelKey: string;
  primary: boolean;
} {
  if (source === "onsite")
    return { labelKey: "widgets.weather.source.onsite", primary: true };
  if (source === "kma")
    return { labelKey: "widgets.weather.source.kma", primary: false };
  if (source === "open-meteo") return { labelKey: "Open-Meteo", primary: false };
  return {
    labelKey: source ? String(source) : "widgets.weather.source.none",
    primary: false,
  };
}
